import Logger from '@/logger'
import LocalStorage from './local-storage'

import { KEYS } from '../data-keys'

/**
 * Remote storage gateway for versioning through migration scripts.
 *
 * @export VersioningStorage
 * @class VersioningStorage
 */
export default class VersioningStorage extends LocalStorage {

    /**
     * Creates an instance of VersioningStorage.
     * @memberof VersioningStorage
     */
    constructor() {
        super({
            keys: [ KEYS.migrations ],
        })
    }

    /**
     * Gets the applied migrations from local storage.
     *
     * @returns {Promise<Array>} The migrations.
     * @memberof VersioningStorage
     */
    async getAsync() {
        return await this.getValueAsync(KEYS.migrations) || { applied: [] }
    }

    /**
     * Registers a migration to add to the array in local storage.
     *
     * @param {Migration} migration The migration to register.
     * @returns {Promise<Object>} The altered data set, adjusted to store.
     * @memberof VersioningStorage
     */
    async registerAsync(migration) {
        const migrations = await this.getAsync()
        migrations.applied = migrations.applied || []
        migrations.applied.push(migration.name)
        Logger.log(`Registered migration [${ migration.name }]`, 'Migrations / Local Storage')
        return await this.upsertDataAsync(KEYS.migrations, migrations)
    }

    /**
     * Registers all migrations to give local storage a current state.
     *
     * @param {Array} migrations The migrations array to register.
     * @returns {Promise<Object>} The altered data set, adjusted to store.
     * @memberof VersioningStorage
     */
    async registerStateAsync(migrations) {
        Logger.logObject(migrations, 'Resetting migrations state', 'Migrations / Local Storage')
        return await this.upsertDataAsync(KEYS.migrations, { applied: migrations })
    }

    /**
     * Removes migrations from the array back to the specified migration.
     *
     * @param {Migration} migration The migration to register.
     * @returns {Promise<Object>} The altered data set, adjusted to store.
     * @memberof VersioningStorage
     */
    async unregisterAsync(migration) {
        const migrations = await this.getAsync()
        let current
        while (migrations.applied && current != migration.name) {
            current = migrations.applied.pop()
            Logger.log(`Unregistered migration [${ current }]`, 'Migrations / Local Storage')
        }
        return await this.upsertDataAsync(KEYS.migrations, migrations)
    }
}
